import React, { useState, useRef, useEffect } from 'react';
import { useForm, useFieldArray, useWatch } from 'react-hook-form';
import { Plus, Trash2 } from 'lucide-react';
import { RoastProfileChart } from '../../components/recipes/RoastProfileChart';
import { profileService, type CreateRoastProfileDTO } from '../../services/profileService';
import { openNativeDatePickerSafely, preventManualDateEntry, preventManualDatePasteOrDrop } from '../../utils/date';
import { useLanguage } from '../../features/language/useLanguage';

interface RoastProfileFormProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  initialData?: any;
  onSuccess?: () => void;
  onCancel?: () => void;
  lotId?: string;
  entityType?: 'ItemType' | 'LotType' | 'SampleType';
  entityId?: string;
}

interface RoastStage {
  name: string;
  temperature: number;
  time: number;
  description?: string;
}

interface RoastProfileFormData {
  roastDate: string;
  notes: string;
  stages: RoastStage[];
}

export const RoastProfileForm: React.FC<RoastProfileFormProps> = ({
  initialData,
  onSuccess,
  onCancel,
  entityType,
  entityId
}) => {
  const { t } = useLanguage();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { register, control, handleSubmit, formState: { errors }, reset } = useForm<RoastProfileFormData>({
    defaultValues: (() => {
      if (initialData) {
        let stages = initialData.stages;
        // Handle case where stages are stored in 'data' string field (legacy/backend format)
        if (!stages && initialData.data && typeof initialData.data === 'string') {
          try {
            stages = JSON.parse(initialData.data);
          } catch (e) {
            console.error("Failed to parse roast profile stages from data string", e);
          }
        }
        return {
          ...initialData,
          stages: Array.isArray(stages) ? stages : [{ name: t('recipes.form.preheat'), temperature: 200, time: 0, description: '' }]
        };
      }
      return {
        roastDate: new Date().toISOString().split('T')[0],
        notes: '',
        stages: [{ name: t('recipes.form.preheat'), temperature: 200, time: 0, description: '' }]
      };
    })()
  });

  // Effect to reset form when initialData changes (e.g., when opening different profiles)
  useEffect(() => {
    if (initialData) {
      let stages = initialData.stages;
      if (!stages && initialData.data && typeof initialData.data === 'string') {
        try {
          stages = JSON.parse(initialData.data);
        } catch (e) {
          console.error("Failed to parse roast profile stages from data string", e);
        }
      }
      
      reset({
        ...initialData,
        stages: Array.isArray(stages) ? stages : [{ name: t('recipes.form.preheat'), temperature: 200, time: 0, description: '' }]
      });
    } else {
       reset({
        roastDate: new Date().toISOString().split('T')[0],
        notes: '',
        stages: [{ name: t('recipes.form.preheat'), temperature: 200, time: 0, description: '' }]
       });
    }
  }, [initialData, reset, t]);

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'stages',
  });

  const stages = useWatch({
    control,
    name: 'stages',
  });

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const content = e.target?.result as string;
        const parsedData = JSON.parse(content);
        
        // Construct the form data from the parsed JSON
        // Using defaults for missing fields to ensure form validity
        const formData: RoastProfileFormData = {
          roastDate: parsedData.roastDate || new Date().toISOString().split('T')[0],
          notes: parsedData.notes || '',
          stages: Array.isArray(parsedData.stages) ? parsedData.stages : [{ name: t('roast.profile.stages.preheat'), temperature: 200, time: 0, description: '' }]
        };

        reset(formData);
      } catch (error) {
        console.error("Error parsing JSON", error);
        alert(t('roast.profileForm.errors.invalidJson'));
      }
    };
    reader.readAsText(file);
    
    // Reset the input value to allow selecting the same file again
    if (event.target) {
        event.target.value = '';
    }
  };

  const onSubmit = async (data: RoastProfileFormData) => {
    setIsSubmitting(true);
    try {
      if (initialData?.id) {
        await profileService.updateRoastProfile(initialData.id, data);
      } else {
        if (!entityType) {
            console.error("Entity Type is missing");
            alert(t('roast.profileForm.errors.entityTypeMissing'));
            return;
        }

        const payload: CreateRoastProfileDTO = {
          ...data,
          type: entityType,
          inventory_item_id: entityType === 'ItemType' ? entityId : undefined,
          lot_id: entityType === 'LotType' ? entityId : undefined,
          sample_id: entityType === 'SampleType' ? entityId : undefined,
          data: JSON.stringify(data.stages)
        };
        await profileService.createRoastProfile(payload);
      }
      onSuccess?.();
    } catch (error) {
      console.error('Failed to save roast profile:', error);
      alert(t('roast.profileForm.errors.saveFailed'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      <div className="space-y-4">
         <div className="flex justify-between items-center">
            <div>
              <input
                type="file"
                accept=".json"
                ref={fileInputRef}
                onChange={handleFileChange}
                className="hidden"
              />
              
            </div>
         </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
                <label className="block text-sm font-medium text-gray-700">{t('roast.profileForm.roastDate')}</label>
                <input
                type="date"
                {...register('roastDate')}
                onClick={openNativeDatePickerSafely}
                onKeyDown={preventManualDateEntry}
                onPaste={preventManualDatePasteOrDrop}
                onDrop={preventManualDatePasteOrDrop}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm border p-2"
                />
            </div>
        </div>

        {/* Chart Section */}
        <div className="mt-4">
             <RoastProfileChart data={stages} />
        </div>

        {/* Roast Stages Section */}
        <div className="border-t pt-4">
            <div className="flex justify-between items-center mb-4">
            <h4 className="text-sm font-medium text-gray-900">{t('recipes.form.roastStages')}</h4>
            <button
                type="button"
                data-test-id="recipe-add-stage-button"
                onClick={() => append({ name: '', temperature: 0, time: 0, description: '' })}
                className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-primary-700 bg-primary-100 hover:bg-primary-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
                <Plus className="h-4 w-4 mr-1" />
                {t('recipes.form.addStage')}
            </button>
            </div>

            <div className="space-y-4">
            {fields.map((field, index) => (
                <div key={field.id} className="flex gap-4 items-start bg-gray-50 p-3 rounded-md" data-test-id={`recipe-stage-row-${index}`}>
                <div className="flex-1 space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                        <label className="block text-xs font-medium text-gray-500">{t('recipes.form.stageName')}</label>
                        <input
                        data-test-id={`recipe-stage-name-input-${index}`}
                        {...register(`stages.${index}.name` as const, { required: t('roast.profileForm.required') })}
                        placeholder={t('recipes.form.stageNamePlaceholder')}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-xs border p-2"
                        />
                         {errors.stages?.[index]?.name && (
                            <p className="mt-1 text-xs text-red-600">{errors.stages[index]?.name?.message}</p>
                        )}
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-gray-500">{t('recipes.form.tempC')}</label>
                        <input
                        data-test-id={`recipe-stage-temp-input-${index}`}
                        type="number"
                        {...register(`stages.${index}.temperature` as const, { valueAsNumber: true, required: t('roast.profileForm.required') })}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-xs border p-2"
                        />
                         {errors.stages?.[index]?.temperature && (
                            <p className="mt-1 text-xs text-red-600">{errors.stages[index]?.temperature?.message}</p>
                        )}
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-gray-500">{t('recipes.form.timeMin')}</label>
                        <input
                        data-test-id={`recipe-stage-time-input-${index}`}
                        type="number"
                        step="0.1"
                        {...register(`stages.${index}.time` as const, { valueAsNumber: true, required: t('roast.profileForm.required') })}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-xs border p-2"
                        />
                         {errors.stages?.[index]?.time && (
                            <p className="mt-1 text-xs text-red-600">{errors.stages[index]?.time?.message}</p>
                        )}
                    </div>
                    </div>
                </div>
                <button
                    type="button"
                    onClick={() => remove(index)}
                    className="mt-6 text-red-600 hover:text-red-800"
                >
                    <Trash2 className="h-5 w-5" />
                </button>
                </div>
            ))}
            </div>
        </div>

        <div>
            <label className="block text-sm font-medium text-gray-700">{t('reports.details.notes')}</label>
            <textarea
            {...register('notes')}
            rows={3}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm border p-2"
            />
        </div>
      </div>

      <div className="flex justify-end space-x-3 pt-4">
        <button
          type="button"
          onClick={onCancel}
          className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
        >
          {t('auth.common.cancel')}
        </button>
        <button
          type="submit"
          disabled={isSubmitting}
          className="inline-flex justify-center rounded-md border border-transparent bg-primary-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubmitting ? t('forms.actions.saving') : t('forms.actions.save')}
        </button>
      </div>
    </form>
  );
};
